import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HeaderLabelCellConfig } from '@brightside/brightside-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BrightsideUiModule } from 'libs/brightside-ui/src/lib/brightside-ui.module';
import { CashAdvanceService } from '../../cash-advance.service';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { CashAdvance } from '../../cash-advance.model';
import { CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';
import { routePath } from 'libs/desktop/data-access/core-services/src/lib/Routing/routing.functions';
import { animate, style, transition, trigger } from '@angular/animations';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'brightside-web-cash-advance-repayments-card',
    imports: [BrightsideUiModule, TranslateModule],
    templateUrl: './cash-advance-repayments-card.component.html',
    styleUrl: './cash-advance-repayments-card.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CashAdvanceService, CurrencyPipe],
    animations: [
        trigger('fadeIn', [transition(':enter', [style({ opacity: 0 }), animate('500ms ease-out', style({ opacity: 1 }))])]),
    ]
})
export class CashAdvanceRepaymentsCardComponent implements OnInit, OnDestroy {
  showRetryAccessory: boolean = false;
  cashAdvanceCellConfig: HeaderLabelCellConfig;

  private _cashAdvanceService: CashAdvanceService = inject(CashAdvanceService);
  private _cashAdvanceInfo$: Observable<CashAdvance | undefined> = this._cashAdvanceService.cashAdvanceInfo$;
  private _cashAdvanceError$: Observable<void> = this._cashAdvanceService.cashAdvanceError$;

  private _translateService: TranslateService = inject(TranslateService);
  private _currencyPipe: CurrencyPipe = inject(CurrencyPipe);
  private _router: Router = inject(Router);

  private _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _unsub$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.setCashAdvanceCardLoading();

    this._cashAdvanceInfo$
      .pipe(
        tap((cashAdvance) => {          
          this.setCashAdvanceInfo(cashAdvance);
        }),
        takeUntil(this._unsub$)
      )
      .subscribe();

    this._cashAdvanceError$
      .pipe(
        tap((error) => {          
          this.handleErrorResponse();
        }),
        takeUntil(this._unsub$)
      )
      .subscribe();

    this._cashAdvanceService.getCashAdvances(true);
  }

  setCashAdvanceInfo(cashAdvance: CashAdvance | undefined): void {    
    this.cashAdvanceCellConfig = {
      leftTitle: this._translateService.instant('CASH_ADVANCE'),
      leftTitleSemibold: true,
      rightIconName: 'chevron_right',
      rightIconColor: 'var(--blue-50)',
      rightSubtitle: cashAdvance?.latest_cash_advance_details?.repay_optout
        ? this._translateService.instant('CASH_ADVANCE_REPAYMENT_STATUS_PAUSED')
        : '',
      rightTitle: this._currencyPipe.transform(cashAdvance?.latest_cash_advance_details?.remaining_balance || 0.0),
      rightTitleSemibold: true,
      rightSubtitleNoWrap: true,
      minHeight: '2.2rem',
      bgColor: 'var(--white)',
    };
    this.showRetryAccessory = false;
    this._cdr.detectChanges();
  }

  navigateToCashAdvanceDashboard(): void {
    this._router.navigate([routePath('cash_advance_dashboard')]);
  }

  retryCashAdvance(): void {
    this.showRetryAccessory = false;
    this.setCashAdvanceCardLoading();
    setTimeout(() => {
      this._cashAdvanceService.getCashAdvances(true);
    }, 3000);    
  }

  ngOnDestroy(): void {
    this._unsub$.next();
  }

  private handleErrorResponse(): void {
    this.cashAdvanceCellConfig = {
      leftTitle: this._translateService.instant('CASH_ADVANCE'),
      leftTitleSemibold: true,
      bgColor: 'var(--white)',
      minHeight: '2.2rem',
    };
    this.showRetryAccessory = true;
    this._cdr.detectChanges();
  }

  private setCashAdvanceCardLoading(): void {
    this.cashAdvanceCellConfig = {
      leftTitle: this._translateService.instant('CASH_ADVANCE'),
      leftTitleSemibold: true,
      bgColor: 'var(--white)',
      lottieLoading: true,
      minHeight: '2.2rem',
    };
  }
}
